import React, { useCallback, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { token } from '@atlaskit/tokens';
import { InteractiveHierarchyModal } from '@atlassian/reporting-lines/interactive-hierarchy-modal';
import { LinkList } from '@atlassian/smart-knowledge-cards/link-list';

import type { Collaborator, PersonProfileData, RecentWorkUrls } from '../../types';
import { appendWorkSubpath } from '../utils/profileWorkUrl';

import { ActionBar } from './ActionBar';
import { Collaborators } from './Collaborators';
import { ExpandableSection } from './ExpandableSection';
import { ProfileIdentity } from './ProfileIdentity';

const messages = defineMessages({
	sectionRecentWork: {
		id: 'user-profile-preview.compact.section-recent-work',
		defaultMessage: 'Recent work',
		description: 'Expandable section header for recent work in the compact user profile preview',
	},
	sectionCollaborators: {
		id: 'user-profile-preview.compact.section-collaborators',
		defaultMessage: 'Collaborators',
		description: 'Expandable section header for collaborators in the compact user profile preview',
	},
});

const styles = cssMap({
	recentWorkContainer: {
		width: '100%',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.large'),
	},
	collaboratorsContainer: {
		width: '100%',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		overflow: 'hidden',
	},
});

interface CompactVariantProps {
	profileData: PersonProfileData;
	recentWorkData?: RecentWorkUrls;
	collaborators?: Collaborator[];
	testId?: string;
	cloudId: string;
	orgId?: string;
	siteId?: string;
	requestingUserId?: string;
	onLinkClick?: (
		url: string,
		index: number,
		opts?: { isAuxClick?: boolean; isContextMenu?: boolean },
	) => void;
}

export function CompactVariant({
	profileData,
	recentWorkData,
	collaborators,
	testId,
	cloudId,
	orgId,
	siteId,
	requestingUserId,
	onLinkClick,
}: CompactVariantProps): React.JSX.Element {
	const intl = useIntl();
	const { fireEvent } = useAnalyticsEvents();
	const [interactiveModalAccountId, setInteractiveModalAccountId] = useState<string | null>(null);
	const handleViewOrgChart = (accountId: string) => setInteractiveModalAccountId(accountId);
	const handleModalClose = () => setInteractiveModalAccountId(null);
	const hasCollaborators = !!collaborators && collaborators.length > 0;
	const showRecentWorkSection = !!(recentWorkData && recentWorkData.length > 0);

	const handleRecentWorkSectionClick = useCallback(
		() => fireEvent('ui.userProfilePreview.clicked.recentWorkSection'),
		[fireEvent],
	);
	const handleCollaboratorsSectionClick = useCallback(
		() => fireEvent('ui.userProfilePreview.clicked.collaboratorsSection'),
		[fireEvent],
	);
	const handleViewAllRecentWorkClick = useCallback(
		() => fireEvent('ui.userProfilePreview.clicked.recentWorkViewAll'),
		[fireEvent],
	);

	return (
		<>
			<Stack space="space.200" testId={testId}>
				<ProfileIdentity
					profileData={profileData}
					avatarSize="xlarge"
					showDepartment
					showLocation
					showPronouns
					showLocalTime
					variant="compact"
					actions={
						<ActionBar
							profileData={profileData}
							cloudId={cloudId}
							onViewOrgChart={handleViewOrgChart}
						/>
					}
				/>

				{showRecentWorkSection && (
					<ExpandableSection
						title={intl.formatMessage(messages.sectionRecentWork)}
						testId="compact-recent-work"
						onClick={handleRecentWorkSectionClick}
					>
						<Box>
							<Box
								testId={testId ? `${testId}-recent-work` : undefined}
								xcss={styles.recentWorkContainer}
							>
								<LinkList
									urls={recentWorkData}
									onLinkClick={onLinkClick}
									size="compact"
									maxSources={3}
									expandedMaxSources={6}
									viewAllUrl={appendWorkSubpath(
										navigateToTeamsApp({
											type: 'USER',
											payload: { userId: profileData.accountId },
											cloudId,
										}).href,
									)}
									onViewAllLinkClick={handleViewAllRecentWorkClick}
								/>
							</Box>
						</Box>
					</ExpandableSection>
				)}

				{hasCollaborators && collaborators && (
					<ExpandableSection
						title={intl.formatMessage(messages.sectionCollaborators)}
						testId="compact-collaborators"
						onClick={handleCollaboratorsSectionClick}
					>
						<Box xcss={styles.collaboratorsContainer}>
							<Collaborators
								collaborators={collaborators}
								variant="compact"
								cloudId={cloudId}
								orgId={orgId}
							/>
						</Box>
					</ExpandableSection>
				)}
			</Stack>

			<ModalTransition>
				{interactiveModalAccountId && orgId && siteId && (
					<InteractiveHierarchyModal
						accountId={interactiveModalAccountId}
						orgId={orgId}
						siteId={siteId}
						cloudId={cloudId}
						onClose={handleModalClose}
						requestingUserId={requestingUserId}
					/>
				)}
			</ModalTransition>
		</>
	);
}
